import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { wallLength, formatDimension } from './floorPlanUtils';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function FloorPlanPropertyPanel({
  selection,
  state,
  onUpdateWall,
  onUpdateOpening,
  onUpdateFurniture,
  onUpdateRoom,
  onDelete,
  unit,
}) {
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();

  // Reset to collapsed when selection changes on mobile
  useEffect(() => {
    if (isMobile) setExpanded(false);
  }, [selection?.element?.id, isMobile]);

  if (!selection) return null;

  const { type, element } = selection;

  // Mobile collapsed view: just name + expand button
  if (isMobile && !expanded) {
    return (
      <div className="floor-plan-property-panel p-2 border-start bg-white d-flex align-items-center justify-content-between">
        <span className="text-capitalize fw-bold">
          <i className={`bi ${getTypeIcon(type)} me-1`}></i>
          {getElementName(type, element)}
        </span>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setExpanded(true)}
          >
            <i className="bi bi-pencil me-1"></i>Edit
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={onDelete}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="floor-plan-property-panel p-3 border-start bg-white overflow-auto">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 text-capitalize">
          <i className={`bi ${getTypeIcon(type)} me-1`}></i>
          {type}
        </h6>
        {isMobile && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-muted"
            onClick={() => setExpanded(false)}
          >
            <i className="bi bi-chevron-down"></i>
          </Button>
        )}
      </div>

      {type === 'wall' && (
        <WallProperties
          wall={element}
          unit={unit}
          onUpdate={onUpdateWall}
        />
      )}

      {type === 'opening' && (
        <OpeningProperties
          opening={element}
          wall={state.walls.find(w => w.id === element.wallId)}
          unit={unit}
          onUpdate={onUpdateOpening}
        />
      )}

      {type === 'furniture' && (
        <FurnitureProperties
          item={element}
          unit={unit}
          onUpdate={onUpdateFurniture}
        />
      )}

      {type === 'room' && (
        <RoomProperties
          room={element}
          onUpdate={onUpdateRoom}
        />
      )}

      <hr />
      <Button
        variant="outline-danger"
        size="sm"
        onClick={onDelete}
        className="w-100"
      >
        <i className="bi bi-trash me-1"></i>Delete {type}
      </Button>
    </div>
  );
}

function getElementName(type, element) {
  switch (type) {
    case 'wall': return 'Wall';
    case 'opening': return element.type === 'door' ? 'Door' : 'Window';
    case 'furniture': return element.label || 'Furniture';
    case 'room': return element.label || 'Room';
    default: return type;
  }
}

function getTypeIcon(type) {
  switch (type) {
    case 'wall': return 'bi-border-outer';
    case 'opening': return 'bi-door-open';
    case 'furniture': return 'bi-lamp';
    case 'room': return 'bi-bounding-box';
    default: return 'bi-circle';
  }
}

function WallProperties({ wall, unit, onUpdate }) {
  const length = wallLength(wall);

  return (
    <>
      <div className="mb-2">
        <small className="text-muted">Length</small>
        <div className="fw-bold">{formatDimension(length, unit)}</div>
      </div>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Thickness (inches)</Form.Label>
        <Form.Control
          type="number"
          size="sm"
          value={wall.thickness}
          min={2}
          max={24}
          onChange={(e) => onUpdate(wall.id, { thickness: Number(e.target.value) })}
        />
      </Form.Group>
    </>
  );
}

function OpeningProperties({ opening, wall, unit, onUpdate }) {
  return (
    <>
      <div className="mb-2">
        <small className="text-muted">Type</small>
        <div className="fw-bold text-capitalize">{opening.type}</div>
      </div>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Width (inches)</Form.Label>
        <Form.Control
          type="number"
          size="sm"
          value={opening.width}
          min={12}
          max={120}
          onChange={(e) => onUpdate(opening.id, { width: Number(e.target.value) })}
        />
      </Form.Group>
      {opening.type === 'door' && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Swing Direction</Form.Label>
            <Form.Select
              size="sm"
              value={opening.direction || 'left'}
              onChange={(e) => onUpdate(opening.id, { direction: e.target.value })}
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Swing</Form.Label>
            <Form.Select
              size="sm"
              value={opening.swing || 'out'}
              onChange={(e) => onUpdate(opening.id, { swing: e.target.value })}
            >
              <option value="out">Outward</option>
              <option value="in">Inward</option>
            </Form.Select>
          </Form.Group>
        </>
      )}
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Position on Wall</Form.Label>
        <Form.Range
          min={0.1}
          max={0.9}
          step={0.01}
          value={opening.position}
          onChange={(e) => onUpdate(opening.id, { position: Number(e.target.value) })}
        />
      </Form.Group>
    </>
  );
}

function FurnitureProperties({ item, unit, onUpdate }) {
  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Label</Form.Label>
        <Form.Control
          type="text"
          size="sm"
          value={item.label || ''}
          onChange={(e) => onUpdate(item.id, { label: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Width (inches)</Form.Label>
        <Form.Control
          type="number"
          size="sm"
          value={item.width}
          min={6}
          max={240}
          onChange={(e) => onUpdate(item.id, { width: Number(e.target.value) })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Depth (inches)</Form.Label>
        <Form.Control
          type="number"
          size="sm"
          value={item.depth}
          min={6}
          max={240}
          onChange={(e) => onUpdate(item.id, { depth: Number(e.target.value) })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Rotation ({item.rotation || 0}°)</Form.Label>
        <Form.Range
          min={0}
          max={360}
          step={15}
          value={item.rotation || 0}
          onChange={(e) => onUpdate(item.id, { rotation: Number(e.target.value) })}
        />
      </Form.Group>
    </>
  );
}

function RoomProperties({ room, onUpdate }) {
  const ROOM_COLORS = [
    { label: 'Light Blue', value: '#E8F0FE' },
    { label: 'Light Green', value: '#E6F4EA' },
    { label: 'Light Yellow', value: '#FEF7E0' },
    { label: 'Light Pink', value: '#FCE8E6' },
    { label: 'Light Purple', value: '#F3E8FD' },
    { label: 'Light Gray', value: '#F1F3F4' },
  ];

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Room Name</Form.Label>
        <Form.Control
          type="text"
          size="sm"
          value={room.label || ''}
          onChange={(e) => onUpdate(room.id, { label: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Color</Form.Label>
        <div className="d-flex gap-1 flex-wrap">
          {ROOM_COLORS.map(c => (
            <button
              key={c.value}
              className="border rounded p-0"
              style={{
                width: 24, height: 24,
                backgroundColor: c.value,
                outline: room.color === c.value ? '2px solid #C4612E' : 'none',
                outlineOffset: 1,
                cursor: 'pointer',
              }}
              title={c.label}
              onClick={() => onUpdate(room.id, { color: c.value })}
            />
          ))}
        </div>
      </Form.Group>
    </>
  );
}

export default React.memo(FloorPlanPropertyPanel);
